import { useDataValue } from 'Simple/Data.js'
import { useMemo } from 'react'
import { localStorage as localStore } from 'Logic/localStorage.js'
import { STORAGE_KEY } from 'Data/constants.js'

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  let questions = useDataValue({
    context: 'widget',
    path: 'questions',
    viewPath: props.viewPath,
  })

  let id = useDataValue({
    context: 'flow_shortcuts',
    viewPath: props.viewPath,
    path: 'id',
  })

  let preview = useDataValue({
    context: 'flow_shortcuts',
    viewPath: props.viewPath,
    path: 'preview',
  })

  let urlParams = useDataValue({
    context: 'flow_shortcuts',
    viewPath: props.viewPath,
  })

  // Read synchronously from localStorage so the correct step and answers are
  // available on the very first render — no async effect, no flash of step 1.
  let previousState = useMemo(() => {
    if (!id || preview === 'true') return null
    try {
      let stored = localStore.getItem(`${STORAGE_KEY}_${id}`)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }, [id, preview])

  let prefillAnswers = useMemo(() => {
    if (!urlParams || !questions) return {}

    let answers = {}
    questions.forEach(question => {
      for (let [key, value] of Object.entries(urlParams)) {
        if (
          question.name?.toLowerCase() === key.toLowerCase() ||
          question.id === key
        ) {
          try {
            answers[question.id] = /^{.+}$/.test(value)
              ? JSON.parse(value)
              : value
          } catch {}
          break
        }
      }
    })
    return answers
  }, [urlParams, questions])

  let newState = useMemo(
    () => ({
      ...data,
      flowQuestions: questions
        .filter(item => !item.hidden)
        .map(item => item.id),
      answers: { ...prefillAnswers, ...data.answers },
    }),
    [questions, data, prefillAnswers]
  )

  return useMemo(() => {
    if (!data) return data

    if (previousState) {
      // If we have stored answers for questions that no longer exist, reset
      let answeredQuestionIds = Object.keys(previousState.answers || {})
      let currentIds = new Set(questions.map(q => q.id))
      let hasStaleAnswers = answeredQuestionIds.some(id => !currentIds.has(id))

      if (hasStaleAnswers) {
        return newState
      }
    }

    return {
      ...(previousState ?? newState),
      // Always use fresh flowQuestions (filters hidden questions from current list)
      flowQuestions: newState.flowQuestions,
    }
  }, [data, previousState, newState, questions])
}
